// src/features/tickets/components/TicketList.tsx
//
// Listen-Komponente fuer Tickets.
// - Reines Praesentations-Layer: bekommt Daten von TicketsPage, rendert sie als
//   Grid-Tabelle. Keine eigene Daten-Logik, kein eigener State.
// - Klick auf eine Zeile triggert onTicketClick.
// - Status-Badge, Priority-Indikator und Aktivitaets-Format als interne Helper;
//   werden in spaeteren Schritten extrahiert, wenn andere Komponenten sie brauchen.

import React from 'react';
import { Inbox, ArrowUp, MessageSquareWarning } from 'lucide-react';
import { TicketListView, TicketStatus } from '../types';

interface TicketListProps {
  tickets: TicketListView[];
  onTicketClick: (ticketId: string) => void;
  currentStaffId: string | null;
}

// CSS-Grid-Spaltenlayout fuer Header und Rows
// (Ticket-Nr) (Betreff, dehnbar) (Kunde) (Status) (Zugewiesen) (Aktivitaet)
const GRID_TEMPLATE = '120px 1fr 200px 120px 100px 140px';

export const TicketList: React.FC<TicketListProps> = ({
  tickets,
  onTicketClick,
  currentStaffId
}) => {
  if (tickets.length === 0) {
    return <EmptyState />;
  }

  return (
    <div style={{
      backgroundColor: 'white',
      borderRadius: '12px',
      border: '1px solid #E5E7EB',
      overflow: 'hidden'
    }}>
      {/* Header-Zeile */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: GRID_TEMPLATE,
        gap: '12px',
        padding: '10px 16px',
        backgroundColor: '#F9FAFB',
        borderBottom: '1px solid #E5E7EB',
        fontSize: '12px',
        fontWeight: '600',
        color: '#6B7280',
        textTransform: 'uppercase',
        letterSpacing: '0.05em'
      }}>
        <div>Ticket</div>
        <div>Betreff</div>
        <div>Kunde</div>
        <div>Status</div>
        <div>Zugewiesen</div>
        <div>Aktivität</div>
      </div>

      {/* Daten-Zeilen */}
      {tickets.map(ticket => (
        <TicketRow
          key={ticket.id}
          ticket={ticket}
          onClick={() => onTicketClick(ticket.id)}
          currentStaffId={currentStaffId}
        />
      ))}
    </div>
  );
};

// ============================================
// Row-Komponente
// ============================================

interface TicketRowProps {
  ticket: TicketListView;
  onClick: () => void;
  currentStaffId: string | null;
}

const TicketRow: React.FC<TicketRowProps> = ({ ticket, onClick, currentStaffId }) => {
  const [hovered, setHovered] = React.useState(false);
  const isMine = currentStaffId !== null && ticket.assigned_to_staff_id === currentStaffId;
  const isCustomerReply = ticket.last_message_from === 'customer';
  const isHighPriority = ticket.priority === 'high';

  // Kundenspalte: Kunde, sonst Firmengruppe mit offener Gesellschaft, sonst Pool
  const customerLabel = ticket.customer
    ? ticket.customer.company
    : (ticket.customer_group ? `${ticket.customer_group.name} (Gesellschaft offen)` : '(Pool)');

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'grid',
        gridTemplateColumns: GRID_TEMPLATE,
        gap: '12px',
        padding: '12px 16px',
        borderBottom: '1px solid #F3F4F6',
        backgroundColor: hovered ? '#F9FAFB' : 'white',
        cursor: 'pointer',
        alignItems: 'center',
        transition: 'background-color 0.1s'
      }}
    >
      {/* Ticket-Nr. */}
      <div style={{
        fontSize: '13px',
        fontFamily: 'monospace',
        color: '#6B7280',
        fontWeight: '500'
      }}>
        {ticket.ticket_number}
      </div>

      {/* Betreff (mit optionaler High-Priority-Markierung + Kundenantwort-Indikator) */}
      <div style={{
        fontSize: '14px',
        color: '#111827',
        fontWeight: isCustomerReply ? '600' : '500',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        minWidth: 0
      }}>
        {isHighPriority && <HighPriorityIcon />}
        <span style={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          flex: 1,
          minWidth: 0
        }}>
          {ticket.subject || '(kein Betreff)'}
        </span>
        {isCustomerReply && <CustomerReplyIcon />}
      </div>

      {/* Kunde */}
      <div
        title={customerLabel}
        style={{
          fontSize: '13px',
          color: ticket.customer ? '#111827' : (ticket.customer_group ? '#374151' : '#9CA3AF'),
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}
      >
        {customerLabel}
      </div>

      {/* Status */}
      <div>
        <StatusBadge status={ticket.status} />
      </div>

      {/* Zugewiesen (Initialen oder Strich) */}
      <div style={{ fontSize: '13px' }}>
        {ticket.assigned_to ? (
          <span
            title={ticket.assigned_to.full_name}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: '28px',
              height: '28px',
              padding: '0 8px',
              borderRadius: '14px',
              backgroundColor: isMine ? '#DBEAFE' : '#F3F4F6',
              color: isMine ? '#1E40AF' : '#374151',
              fontSize: '12px',
              fontWeight: '600'
            }}
          >
            {ticket.assigned_to.initials}
          </span>
        ) : (
          <span style={{ color: '#9CA3AF' }}>—</span>
        )}
      </div>

      {/* Aktivitaet */}
      <div style={{
        fontSize: '13px',
        color: '#6B7280'
      }}>
        {formatActivity(ticket.last_message_at || ticket.created_at)}
      </div>
    </div>
  );
};

// ============================================
// Status-Badge (inline)
// ============================================

interface StatusConfig {
  label: string;
  bg: string;
  fg: string;
}

const STATUS_CONFIG: Record<TicketStatus, StatusConfig> = {
  new: { label: 'Neu', bg: '#DBEAFE', fg: '#1E40AF' },
  open: { label: 'Offen', bg: '#DBEAFE', fg: '#1E40AF' },
  pending: { label: 'Wartet', bg: '#FEF3C7', fg: '#92400E' },
  resolved: { label: 'Gelöst', bg: '#D1FAE5', fg: '#065F46' },
  closed: { label: 'Geschlossen', bg: '#F3F4F6', fg: '#6B7280' }
};

const StatusBadge: React.FC<{ status: TicketStatus }> = ({ status }) => {
  const config = STATUS_CONFIG[status];
  return (
    <span style={{
      display: 'inline-block',
      padding: '3px 10px',
      borderRadius: '12px',
      backgroundColor: config.bg,
      color: config.fg,
      fontSize: '12px',
      fontWeight: '600',
      whiteSpace: 'nowrap'
    }}>
      {config.label}
    </span>
  );
};

// ============================================
// High-Priority-Icon (single-purpose, kein Prop)
// ============================================

const HighPriorityIcon: React.FC = () => (
  <span
    title="Hohe Priorität"
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      flexShrink: 0
    }}
  >
    <ArrowUp size={14} color="#DC2626" />
  </span>
);

// ============================================
// Customer-Reply-Icon (Indikator fuer neue Kundenantwort)
// ============================================

const CustomerReplyIcon: React.FC = () => (
  <span
    title="Letzte Aktivität: Kundenantwort"
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      flexShrink: 0
    }}
  >
    <MessageSquareWarning size={14} color="#D97706" />
  </span>
);

// ============================================
// Empty-State
// ============================================

const EmptyState: React.FC = () => {
  return (
    <div style={{
      backgroundColor: 'white',
      borderRadius: '12px',
      border: '1px solid #E5E7EB',
      padding: '60px 20px',
      textAlign: 'center',
      color: '#6B7280'
    }}>
      <Inbox size={40} color="#9CA3AF" style={{ marginBottom: '12px' }} />
      <div style={{ fontSize: '15px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
        Keine Tickets gefunden
      </div>
      <div style={{ fontSize: '13px' }}>
        Mit den aktuellen Filtern gibt es nichts anzuzeigen.
      </div>
    </div>
  );
};

// ============================================
// Activity-Format-Helper
// ============================================

/**
 * Formatiert einen Timestamp als kurze Aktivitaets-Anzeige:
 *   - Heute:       "10:32"
 *   - Gestern:     "Gestern 14:20"
 *   - Diese Woche: "Mo 09:15"
 *   - Aelter:      "12.05.2026"
 */
function formatActivity(isoTimestamp: string | null): string {
  if (!isoTimestamp) return '—';

  const date = new Date(isoTimestamp);
  if (Number.isNaN(date.getTime())) return '—';

  const now = new Date();
  const isSameDay =
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate();

  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  const isYesterday =
    date.getFullYear() === yesterday.getFullYear() &&
    date.getMonth() === yesterday.getMonth() &&
    date.getDate() === yesterday.getDate();

  const sixDaysAgo = new Date(now);
  sixDaysAgo.setDate(sixDaysAgo.getDate() - 6);
  const isThisWeek = date >= sixDaysAgo;

  const hh = String(date.getHours()).padStart(2, '0');
  const mm = String(date.getMinutes()).padStart(2, '0');

  if (isSameDay) {
    return `${hh}:${mm}`;
  }
  if (isYesterday) {
    return `Gestern ${hh}:${mm}`;
  }
  if (isThisWeek) {
    const weekdays = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
    return `${weekdays[date.getDay()]} ${hh}:${mm}`;
  }

  const dd = String(date.getDate()).padStart(2, '0');
  const monat = String(date.getMonth() + 1).padStart(2, '0');
  return `${dd}.${monat}.${date.getFullYear()}`;
}
